import { configureStore, ThunkAction, Action } from '@reduxjs/toolkit'
import AuthenticationReducer from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { setupListeners } from '@reduxjs/toolkit/dist/query'
import { ProgressAPI } from '@Features/Progress/ProgressSlice/ProgressSlice'
import settingsSlice from '@Features/Settings/SettingsSlice/Settings.slice'
import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import SnackbarSlice from './SnackbarSlice/SnackbarSlice'
import RunningTestSlice from './RunningTestSlice/RunningTestSlice'
import UtilsSlice from './UtilsSlice/UtilsSlice'
import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync'

/**
 * RTK Query INTERNAL actions are tab-private and must never cross the
 * state-sync channel.
 *
 * The lifecycle actions' `meta.baseQueryMeta` holds the live `Request`/
 * `Response` objects fetchBaseQuery attaches, which are not
 * structured-cloneable — so broadcasting one throws DataCloneError, once per
 * completed request (hundreds per page).
 *
 * The cross-tab damage is worse than the noise: `pending` clones fine while
 * `fulfilled` does not, so a foreign tab's `pending` overwrites the local
 * `substate.requestId` and the local `fulfilled` is then discarded by RTK
 * Query's own guard — leaving entries stuck loading whenever two tabs are open.
 * Subscription and cache-collection bookkeeping is per-tab for the same
 * reason: one tab expiring an entry must not evict another tab's live copy.
 *
 * Two util actions are deliberately let through — they carry only plain
 * payloads and other tabs depend on them: logout broadcasts `resetApiState`
 * so the signed-out student's cached profile/wallet data is purged everywhere,
 * and login/signup broadcast `invalidateTags` so other tabs refresh their
 * guest lists. The first cut of this predicate blocked the whole reducerPath
 * prefix and silently lost that purge.
 *
 * `auth/login/rejected` and `auth/signup/rejected` are blocked for the clone
 * reason from the other side: their rejectWithValue payload is the live axios
 * response (an XHR in `.request`, functions in `.config`), and
 * broadcast-channel posts inside a microtask, so the DataCloneError surfaced
 * as an unhandled promise rejection on every failed login. No other tab has
 * any use for a failed login.
 *
 * A predicate rather than a blacklist: RTK Query mints action types
 * dynamically, so an explicit deny-list would silently miss any new API root.
 */
const rtkQueryActionPrefixes = [`${BaseApi.reducerPath}/`, `${ProgressAPI.reducerPath}/`]
const crossTabAllowedTypes = new Set([
	`${BaseApi.reducerPath}/resetApiState`,
	`${BaseApi.reducerPath}/invalidateTags`,
	`${ProgressAPI.reducerPath}/resetApiState`,
	`${ProgressAPI.reducerPath}/invalidateTags`,
])
const crossTabBlockedTypes = new Set(['auth/login/rejected', 'auth/signup/rejected'])

export const store = configureStore({
	reducer: {
		auth: AuthenticationReducer,
		// No entry for classroomApi or dashboardAPI. Both are
		// `BaseApi.enhanceEndpoints().injectEndpoints()`, which returns the SAME
		// api instance rather than a new one — so `classroomApi.reducerPath` IS
		// `BaseApi.reducerPath`, and registering both put the identical key in
		// this object twice. Harmless at runtime, since the duplicate resolved to
		// the same reducer, but it read as though there were two separate APIs.
		[SnackbarSlice.name]: SnackbarSlice.reducer,
		[RunningTestSlice.name]: RunningTestSlice.reducer,
		[ProgressAPI.reducerPath]: ProgressAPI.reducer,
		[settingsSlice.name]: settingsSlice.reducer,
		[BaseApi.reducerPath]: BaseApi.reducer,
		[UtilsSlice.name]: UtilsSlice.reducer,
	},
	// serializableCheck stays off: CompletedUnitsWidget sends live `Date` objects
	// as the from/to of its request body, so the check would flag legitimate
	// payloads. Worth knowing it is NOT what would have caught the DataCloneError
	// above — that came from the state-sync channel, not from the store.
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({ serializableCheck: false })
			.concat(BaseApi.middleware)
			// ProgressAPI has a reducer registered above but never had its
			// middleware, so that root ran with no cache-lifetime management, no tag
			// invalidation and no refetchOnReconnect — its subscriptions were never
			// collected.
			.concat(ProgressAPI.middleware)
			.concat(
				createStateSyncMiddleware({
					predicate: action => {
						const type = String(action.type)
						if (crossTabBlockedTypes.has(type)) return false
						if (crossTabAllowedTypes.has(type)) return true
						return !rtkQueryActionPrefixes.some(prefix => type.startsWith(prefix))
					},
				})
			),
	devTools: true,
})

setupListeners(store.dispatch)
initMessageListener(store)

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>
